class ProviderLocationMap {
  constructor() {
    this.config = this.parseEmbeddedJson();
    this.elements = this.queryDomElements();

    if (this.elements.mapContainer && this.elements.mapNavigationData) {
      this.loadMapboxAssets().then(() => {
        this.createMap();
      });
    }
  }

  queryDomElements = () => {
    return {
      mapContainer: document.getElementById("provider-location-map"),
      mapNavigationData: document.getElementById("map-navigation-data"),
    };
  };

  parseEmbeddedJson = () => {
    const mapConfig = JSON.parse(
      document.getElementById("mapbox-config").textContent,
    );
    if (!mapConfig) throw new Error("unable to load mapbox config");
    return mapConfig;
  };

  createMap() {
    if (!this.elements.mapNavigationData) return;

    const mapNavigationData = JSON.parse(
      this.elements.mapNavigationData.textContent,
    );

    const {
      provider_latitude: providerLatitude,
      provider_longitude: providerLongitude,
      nearest_subway_station: nearestSubwayStation,
    } = mapNavigationData;
    if (!providerLatitude || !providerLongitude) return;

    mapboxgl.accessToken = this.config.mapboxToken;

    const map = new mapboxgl.Map({
      container: "provider-location-map",
      style: "mapbox://styles/mapbox/streets-v12",
      center: [providerLongitude, providerLatitude],
      zoom: mapNavigationData.zoom ?? 13,
      interactive: true,
    });

    // Add a marker for the provider location
    const isV2 = this.elements.mapContainer.hasAttribute("data-v2");
    const markerOptions = isV2
      ? { color: "#147F85", scale: 0.78 }
      : { color: "#067AA3" };
    new mapboxgl.Marker(markerOptions)
      .setLngLat([providerLongitude, providerLatitude])
      .addTo(map);

    // Add subway station marker if available
    if (nearestSubwayStation) {
      this.addSubwayStationMarker(map, nearestSubwayStation);

      // Wait for the map style to load before adding sources
      map.on("load", () => {
        this.addWalkingDirections(
          map,
          providerLatitude,
          providerLongitude,
          nearestSubwayStation.station.latitude,
          nearestSubwayStation.station.longitude,
        );
      });
    }

    this.map = map;
  }

  loadMapboxJs() {
    return new Promise((resolve, reject) => {
      // Check if mapboxgl is already loaded
      if (window.mapboxgl) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = this.config.mapboxJsUrl;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  loadMapboxCss() {
    return new Promise((resolve, reject) => {
      // Check if CSS is already loaded
      const existingLink = document.querySelector(
        `link[href="${this.config.mapboxCssUrl}"]`,
      );
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = this.config.mapboxCssUrl;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  async loadMapboxAssets() {
    await Promise.all([this.loadMapboxCss(), this.loadMapboxJs()]);
  }

  addSubwayStationMarker(map, stationData) {
    // Create subway train icon marker
    const el = document.createElement("div");
    el.className = "subway-station-marker";
    el.style.cssText = `
      width: 28px;
      height: 28px;
    `;
    el.innerHTML = "\u{1F687}";

    new mapboxgl.Marker({ element: el })
      .setLngLat([stationData.station.longitude, stationData.station.latitude])
      .addTo(map);
  }

  addWalkingDirections(
    map,
    originLatitude,
    originLongitude,
    targetLatitude,
    targetLongitude,
  ) {
    // Use Mapbox Directions API to get walking route
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/walking/${originLongitude},${originLatitude};${targetLongitude},${targetLatitude}`;
    const params = new URLSearchParams({
      access_token: this.config.mapboxToken,
      geometries: "geojson",
      overview: "full",
    });

    fetch(`${directionsUrl}?${params}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.routes && data.routes.length > 0) {
          const route = data.routes[0];

          // Add walking route as dotted line
          map.addSource("walking-route", {
            type: "geojson",
            data: {
              type: "Feature",
              properties: {},
              geometry: route.geometry,
            },
          });

          map.addLayer({
            id: "walking-route",
            type: "line",
            source: "walking-route",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#067AA3",
              "line-width": 3,
              "line-dasharray": [2, 2], // Creates dotted line
            },
          });

          // Fit map to show both points and route, but maintain reasonable zoom
          const coordinates = route.geometry.coordinates;
          const bounds = coordinates.reduce(
            (bounds, coord) => {
              return bounds.extend(coord);
            },
            new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
          );

          map.fitBounds(bounds, {
            padding: 40,
            maxZoom: 14,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching walking directions:", error);
      });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const hasMapContainer = document.getElementById("provider-location-map");

  if (!hasMapContainer) return;

  if (!document.getElementById("mapbox-config")) {
    throw new Error(
      "Map container exists but mapbox-config is missing. This indicates a template bug.",
    );
  }

  new ProviderLocationMap();
});
